import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { ChatTextIcon } from "@phosphor-icons/react/dist/csr/ChatText";
import { NotePencilIcon } from "@phosphor-icons/react/dist/csr/NotePencil";
import type { ReactNode } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import type { PayPeriodLockTarget } from "@/payroll/pay-periods/pay-period-lock";
import { PayBreakdownMoreActionsMenu } from "./pay-breakdown-more-actions-menu";
import {
  isPayBreakdownRerunMenuActionVisible,
  type PayBreakdownRerunMenuAction,
} from "./pay-breakdown-rerun-action";
import type {
  PayBreakdownCommentsAction,
  PayBreakdownDownloadAction,
  PayBreakdownManualEditsAction,
  PayBreakdownNextPeriodNoteAction,
} from "./types";

interface PayBreakdownWorkbookHeaderActions {
  download: PayBreakdownDownloadAction;
  manualEdits?: PayBreakdownManualEditsAction;
  comments?: PayBreakdownCommentsAction;
  approvalRequest?: ReactNode;
}

interface PayBreakdownHeaderActionsProps {
  workbook?: PayBreakdownWorkbookHeaderActions;
  nextPeriodNote?: PayBreakdownNextPeriodNoteAction;
  explanationChat?: {
    onOpen: () => void;
    placement: "button" | "menu";
  };
  rerun?: PayBreakdownRerunMenuAction;
  lock?: PayPeriodLockTarget;
  isLocked: boolean;
}

export const PayBreakdownHeaderActions = ({
  workbook,
  nextPeriodNote,
  explanationChat,
  rerun,
  lock,
  isLocked,
}: PayBreakdownHeaderActionsProps) => {
  const hasEditsToReview = (workbook?.manualEdits?.reviewCount ?? 0) > 0;
  const showManualEditsReview = hasEditsToReview && !isLocked;
  const hasRerun = isPayBreakdownRerunMenuActionVisible(rerun);
  // Like manual edits: a header button while there is something open to read,
  // otherwise a More actions entry.
  const hasOpenComments = (workbook?.comments?.openCount ?? 0) > 0;

  if (!workbook && !explanationChat && !hasRerun && !lock && !nextPeriodNote)
    return null;

  return (
    <Stack
      direction="row"
      align="center"
      justify="end"
      gap={2}
      className="flex-wrap"
    >
      {workbook && (
        <>
          {workbook.approvalRequest}
          {workbook.comments && hasOpenComments && (
            <Button variant="secondary" onClick={workbook.comments.onOpen}>
              <ChatTextIcon data-icon="inline-start" />
              Comments
              <Badge
                variant="secondary"
                color="orange"
                size="sm"
                className="ml-1.5"
              >
                {workbook.comments.openCount}
              </Badge>
            </Button>
          )}
          {workbook.manualEdits && showManualEditsReview && (
            <Button variant="secondary" onClick={workbook.manualEdits.onOpen}>
              <NotePencilIcon data-icon="inline-start" />
              Manual edits
              <Badge
                variant="secondary"
                color="warning"
                size="sm"
                className="ml-1.5"
              >
                {workbook.manualEdits.reviewCount}
              </Badge>
            </Button>
          )}
        </>
      )}
      {explanationChat?.placement === "button" && (
        <Button variant="secondary" onClick={explanationChat.onOpen}>
          <ChatCircleIcon data-icon="inline-start" />
          Ask AI
        </Button>
      )}
      <PayBreakdownMoreActionsMenu
        nextPeriodNote={nextPeriodNote}
        onOpenExplanationChat={
          explanationChat?.placement === "menu"
            ? explanationChat.onOpen
            : undefined
        }
        download={workbook?.download}
        onOpenManualEdits={
          workbook?.manualEdits && (!hasEditsToReview || isLocked)
            ? workbook.manualEdits.onOpen
            : undefined
        }
        onOpenComments={
          workbook?.comments && !hasOpenComments
            ? workbook.comments.onOpen
            : undefined
        }
        rerun={rerun}
        lock={lock}
      />
    </Stack>
  );
};
